import { ComponentType, ComponentProps, ChangeEvent } from "react";
import { BaseFieldProps, WrappedFieldProps, Field } from "redux-form";

type RequiredPropertiesOf<T extends any> = Exclude<
  {
    [K in keyof T]: T extends Record<K, T[K]> ? K : never;
  }[keyof T],
  undefined
>;

type IfEquals<T, U, Y = unknown, N = never> = (<G>() => G extends T
  ? 1
  : 2) extends <G>() => G extends U ? 1 : 2
  ? Y
  : N;

type TypedFieldOnChangeFunction = (
  event: ChangeEvent<any>,
  newValue: any,
  previousValue: any,
  fieldName: string
) => void;

interface PropsWithOptionalProps<
  S extends ComponentType<P>,
  P = any,
  O = Omit<ComponentProps<S>, keyof WrappedFieldProps>
> extends Omit<BaseFieldProps<P>, "component" | "props" | "onChange"> {
  component: S;
  props?: O;
  onChange?: TypedFieldOnChangeFunction;
  onSwitch?: () => void;
}

interface PropsWithRequiredProps<
  S extends ComponentType<P>,
  P = any,
  O = Omit<ComponentProps<S>, keyof WrappedFieldProps>
> extends Omit<BaseFieldProps<P>, "component" | "props" | "onChange"> {
  component: S;
  props: O;
  onChange?: TypedFieldOnChangeFunction;
  onSwitch?: () => void;
}

const TypedField = <
  S extends ComponentType<P>,
  P = any,
  O = Omit<ComponentProps<S>, keyof WrappedFieldProps>,
  R = RequiredPropertiesOf<O>
>({
  onChange,
  ...props
}: IfEquals<
  R,
  never,
  PropsWithOptionalProps<S, P>,
  PropsWithRequiredProps<S, P>
>) => <Field {...props} onChange={onChange as any} />;

export default TypedField;
